/**
 * LibraryScreen — Discover surface. Searchable, filterable retreat catalogue.
 *
 * UI-INV-005: Leaf component, props only, no engine imports.
 * All filtering is client-side — O(n) is fine for < 20 retreats in beta.
 */
import { useState, useMemo } from "react";
import type { EditableRetreatSchedule } from "@retreat/core";
import styles from "./LibraryScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { totalSeconds } from "../utils/scheduleUtils";
import { SCHEDULE_FACETS, TRADITION_COLORS } from "../constants";
import type { UserSchedule } from "../state/userScheduleRepository";

export type GoodForTag = { id: string; label: string };

export type DiscoverOption = {
  schedule: EditableRetreatSchedule;
  label: string;
  tradition: "zen" | "vipassana" | "secular";
  traditionLabel: string;
  summary: string | null;
  goodFor: GoodForTag[];
};

type Tradition = "all" | "zen" | "vipassana" | "secular" | "mine";
type Facet = "under-hour" | "half-day" | "full-day" | "multi-day" | "gentle" | "first-timers";

type TraditionPill = { id: Tradition; label: string };

const TRADITION_PILLS: TraditionPill[] = [
  { id: "all", label: "All" },
  { id: "zen", label: "Sōtō Zen" },
  { id: "vipassana", label: "Vipassana" },
  { id: "secular", label: "Secular" },
  { id: "mine", label: "Mine" },
];

const FACET_LABELS: Record<Facet, string> = {
  "under-hour": "Under an hour",
  "half-day": "½ day",
  "full-day": "Full day",
  "multi-day": "Multi-day",
  "gentle": "Gentle",
  "first-timers": "For first-timers",
};

// Use canonical tradition colors from constants
const TRADITION_DOTS = TRADITION_COLORS;

export function matchFacet(schedule: EditableRetreatSchedule, goodFor: GoodForTag[], facet: Facet): boolean {
  const secs = totalSeconds(schedule);
  switch (facet) {
    case "under-hour":   return secs < SCHEDULE_FACETS.UNDER_HOUR_MAX_SECONDS;
    case "half-day":     return secs >= SCHEDULE_FACETS.UNDER_HOUR_MAX_SECONDS && secs < SCHEDULE_FACETS.HALF_DAY_MAX_SECONDS;
    case "full-day":     return secs >= SCHEDULE_FACETS.HALF_DAY_MAX_SECONDS && schedule.days.length === 1;
    case "multi-day":    return schedule.days.length > 1;
    case "gentle":       return goodFor.some((g) => g.id === "gentle");
    case "first-timers": return goodFor.some((g) => g.id === "first-timers");
  }
}

function matchSearch(option: DiscoverOption, query: string): boolean {
  const q = query.toLowerCase();
  return (
    option.schedule.name.toLowerCase().includes(q) ||
    option.label.toLowerCase().includes(q) ||
    (option.summary?.toLowerCase().includes(q) ?? false) ||
    option.goodFor.some((g) => g.label.toLowerCase().includes(q)) ||
    option.traditionLabel.toLowerCase().includes(q)
  );
}

type Props = {
  options: DiscoverOption[];
  onSelect: (schedule: EditableRetreatSchedule) => void;
  onForkSchedule?: (schedule: EditableRetreatSchedule) => void;
  onOpenSettings: () => void;
  onBack: () => void;
  /** Pre-activate a facet on first open (e.g. "first-timers" for new users) */
  initialFacet?: Facet;
  /** Pre-select a tradition filter on first open (e.g. from a HomeScreen tradition door or after save). */
  initialTradition?: string;
  /** User's personal saved schedules (soft-deleted entries already excluded by caller). */
  personalSchedules: UserSchedule[];
  onNewSchedule: () => void;
  onEditPersonalSchedule: (entry: UserSchedule) => void;
  onViewPersonalSchedule: (entry: UserSchedule) => void;
  onDeletePersonalSchedule: (id: string) => void;
};

export function LibraryScreen({
  options,
  onSelect,
  onForkSchedule,
  onOpenSettings,
  onBack,
  initialFacet,
  initialTradition,
  personalSchedules,
  onNewSchedule,
  onEditPersonalSchedule,
  onViewPersonalSchedule,
  onDeletePersonalSchedule,
}: Props) {
  const [query, setQuery] = useState("");
  const [tradition, setTradition] = useState<Tradition>(() => {
    if (initialTradition === "mine") return "mine";
    if (initialTradition) {
      const match = TRADITION_PILLS.find((p) => p.id === initialTradition && p.id !== "all");
      if (match) return match.id;
    }
    return "all";
  });
  const [activeFacets, setActiveFacets] = useState<Set<Facet>>(
    () => (initialFacet ? new Set([initialFacet]) : new Set()),
  );
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const isMine = tradition === "mine";

  const filtered = useMemo(() => {
    if (isMine) return [];
    return options
      .filter((o) => tradition === "all" || o.tradition === tradition)
      .filter((o) => activeFacets.size === 0 || [...activeFacets].every((f) => matchFacet(o.schedule, o.goodFor, f)))
      .filter((o) => query === "" || matchSearch(o, query));
  }, [options, tradition, activeFacets, query, isMine]);

  function toggleFacet(f: Facet) {
    setActiveFacets((prev) => {
      const next = new Set(prev);
      next.has(f) ? next.delete(f) : next.add(f);
      return next;
    });
  }

  function clearAll() {
    setQuery("");
    setTradition("all");
    setActiveFacets(new Set());
  }

  function handleDelete(id: string) {
    setDeleteConfirmId(null);
    onDeletePersonalSchedule(id);
  }

  const hasFilters = query !== "" || tradition !== "all" || activeFacets.size > 0;

  return (
    <div className={styles.screen} data-testid={TEST_IDS.LIBRARY_SCREEN}>
      {/* MetaStrip */}
      <div className={styles.meta}>
        <button className={styles.backBtn} onClick={onBack} type="button">
          ← back
        </button>
        <button className={styles.gearBtn} onClick={onOpenSettings} type="button" aria-label="Settings">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.4" />
            <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      <div className={styles.content}>
        <h1 className={styles.heading}>{isMine ? "Your schedules." : "Find a practice."}</h1>
        <p className={styles.whisper}>
          {isMine
            ? "Saved retreats you've built or forked. Tap a card to start or edit."
            : "Search by name, time you have, or what you're sitting with."}
        </p>

        {/* Search bar — hidden in Mine view */}
        {!isMine && (
          <div className={styles.searchWrap}>
            <span className={styles.searchIcon} aria-hidden="true">🔍</span>
            <input
              className={styles.searchInput}
              type="search"
              placeholder="grief · zazen · 20 minutes…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid={TEST_IDS.DISCOVER_SEARCH}
              aria-label="Search retreats"
            />
            {query && (
              <button
                className={styles.searchClear}
                onClick={() => setQuery("")}
                type="button"
                aria-label="Clear search"
                data-testid={TEST_IDS.DISCOVER_SEARCH_CLEAR}
              >
                ×
              </button>
            )}
          </div>
        )}

        {/* Tradition rail — includes Mine pill */}
        <div className={styles.tradRail} data-testid={TEST_IDS.DISCOVER_TRAD_RAIL}>
          {TRADITION_PILLS.map((pill) => {
            const active = tradition === pill.id;
            return (
              <button
                key={pill.id}
                className={`${styles.tradPill} ${active ? styles.tradPillActive : ""}`}
                onClick={() => setTradition(pill.id)}
                type="button"
                data-tradition={pill.id}
                data-testid={pill.id === "mine" ? TEST_IDS.DISCOVER_MINE_PILL : undefined}
              >
                {pill.label}
              </button>
            );
          })}
        </div>

        {/* Facet chips — hidden in Mine view */}
        {!isMine && (
          <div className={styles.facets} data-testid={TEST_IDS.DISCOVER_FACETS}>
            {(Object.keys(FACET_LABELS) as Facet[]).map((f) => (
              <button
                key={f}
                className={`${styles.facet} ${activeFacets.has(f) ? styles.facetActive : ""}`}
                onClick={() => toggleFacet(f)}
                type="button"
                data-facet={f}
              >
                {FACET_LABELS[f]}
              </button>
            ))}
          </div>
        )}

        {/* ── Mine section ─────────────────────────────────────────────── */}
        {isMine ? (
          <div data-testid={TEST_IDS.DISCOVER_MINE_SECTION}>
            {/* New schedule button — always visible at top */}
            <button
              className={styles.mineNewBtn}
              onClick={onNewSchedule}
              type="button"
              data-testid={TEST_IDS.DISCOVER_MINE_NEW_BTN}
            >
              + New schedule
            </button>

            {personalSchedules.length === 0 ? (
              <div className={styles.mineEmpty} data-testid={TEST_IDS.DISCOVER_MINE_EMPTY}>
                <p className={styles.mineEmptyHeading}>No saved schedules yet.</p>
                <p className={styles.mineEmptyBody}>
                  Fork any retreat from the library or start fresh — your schedules live here.
                </p>
              </div>
            ) : (
              <div className={styles.mineCards}>
                {personalSchedules.map((entry) => {
                  const secs = totalSeconds(entry.schedule);
                  const isMultiDay = entry.schedule.days.length > 1;
                  const cardTradition = entry.schedule.tradition ?? "secular";
                  const dotColor = TRADITION_DOTS[cardTradition as keyof typeof TRADITION_DOTS] ?? TRADITION_DOTS.secular;
                  const isConfirmingDelete = deleteConfirmId === entry.schedule.id;

                  return (
                    <div
                      key={entry.schedule.id}
                      className={`${styles.mineCard} ${isConfirmingDelete ? styles.mineCardActive : ""}`}
                      data-testid={TEST_IDS.DISCOVER_MINE_CARD}
                    >
                      {/* Card main tap area — opens detail */}
                      <button
                        className={styles.mineCardBody}
                        onClick={() => onViewPersonalSchedule(entry)}
                        type="button"
                      >
                        <div className={styles.mineCardHeader}>
                          <span className={styles.tradDot} style={{ background: dotColor }} aria-hidden="true" />
                          <span className={styles.mineCardName}>{entry.schedule.name || "Untitled schedule"}</span>
                        </div>
                        <p className={styles.mineCardMeta}>
                          {formatDuration(secs)}
                          {isMultiDay && <> · {entry.schedule.days.length} days</>}
                          {entry.baseScheduleId && <span className={styles.mineCardForkLabel}> · adapted</span>}
                        </p>
                      </button>

                      {/* Action buttons — Edit + Remove directly, no ⋯ menu */}
                      <div className={styles.mineCardActions}>
                        <button
                          className={styles.mineEditBtn}
                          onClick={() => onEditPersonalSchedule(entry)}
                          type="button"
                          data-testid={TEST_IDS.DISCOVER_MINE_EDIT_BTN}
                        >
                          Edit
                        </button>
                        <button
                          className={styles.mineRemoveBtn}
                          onClick={() => setDeleteConfirmId(isConfirmingDelete ? null : entry.schedule.id)}
                          type="button"
                          aria-label={isConfirmingDelete ? "Cancel" : "Remove schedule"}
                          data-testid={TEST_IDS.DISCOVER_MINE_REMOVE_BTN}
                        >
                          {isConfirmingDelete ? "×" : "Remove"}
                        </button>
                      </div>

                      {/* Inline delete confirmation */}
                      {isConfirmingDelete && (
                        <div className={styles.mineDeleteConfirm}>
                          <p className={styles.mineDeletePrompt}>Remove this schedule?</p>
                          <div className={styles.mineDeleteRow}>
                            <button
                              className={styles.mineDeleteCancel}
                              onClick={() => setDeleteConfirmId(null)}
                              type="button"
                            >
                              Cancel
                            </button>
                            <button
                              className={styles.mineDeleteConfirmBtn}
                              onClick={() => handleDelete(entry.schedule.id)}
                              type="button"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* ── Curated catalogue ─────────────────────────────────────── */
          <>
            <p className={styles.eyebrow} data-testid={TEST_IDS.DISCOVER_COUNT}>
              {filtered.length} {filtered.length === 1 ? "retreat" : "retreats"} · curated
            </p>

            {filtered.length === 0 ? (
              <div className={styles.empty} data-testid={TEST_IDS.DISCOVER_EMPTY}>
                <p className={styles.emptyText}>No retreats match</p>
                {hasFilters && (
                  <button className={styles.clearLink} onClick={clearAll} type="button">
                    Clear all filters
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.cards} data-testid={TEST_IDS.DISCOVER_CARDS}>
                {filtered.map((opt) => {
                  const secs = totalSeconds(opt.schedule);
                  const isMultiDay = opt.schedule.days.length > 1;
                  const dotColor = TRADITION_DOTS[opt.tradition] ?? TRADITION_DOTS.secular;
                  return (
                    <div
                      key={`${opt.tradition}-${opt.schedule.id}`}
                      className={styles.cardWrap}
                      data-testid={TEST_IDS.DISCOVER_CARD}
                    >
                      <button
                        className={styles.card}
                        onClick={() => onSelect(opt.schedule)}
                        type="button"
                      >
                        <div className={styles.cardHeader}>
                          <span className={styles.tradDot} style={{ background: dotColor }} aria-hidden="true" />
                          <span className={styles.cardName}>{opt.label}</span>
                        </div>
                        <p className={styles.cardMeta}>
                          {opt.traditionLabel} · {formatDuration(secs)}
                          {isMultiDay && <span className={styles.multiDay}> · multi-day</span>}
                        </p>
                        {opt.summary && (
                          <p className={styles.cardSummary}>{opt.summary}</p>
                        )}
                        {opt.goodFor.length > 0 && (
                          <div className={styles.goodForRow}>
                            {opt.goodFor.map((g) => (
                              <span key={g.id} className={styles.goodForChip}>
                                good for · {g.label}
                              </span>
                            ))}
                          </div>
                        )}
                      </button>
                      {onForkSchedule && (
                        <button
                          className={styles.cardForkBtn}
                          onClick={() => onForkSchedule(opt.schedule)}
                          type="button"
                        >
                          Edit →
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Tap-away overlay to dismiss delete confirmation */}
      {deleteConfirmId && (
        <div
          className={styles.mineOverlay}
          onClick={() => setDeleteConfirmId(null)}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

function formatDuration(secs: number): string {
  if (secs < 3600) return `${Math.round(secs / 60)} min`;
  const h = Math.floor(secs / 3600);
  const m = Math.round((secs % 3600) / 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
